import { PropsWithChildren, useMemo } from "react"
import { useLocalization } from "@tm/localization"
import { Box, CellContentPosition, LicensePlate, TableCellData, TableColumnData } from "@tm/components"
import { OrderIdSource, OrderVoucherList, VoucherType } from "@tm/models"
import { NoResultHint } from "../../_shared/NoResultHint"
import { StyledTableWithoutPadding } from "../../_shared/StyledComponents"
import { VoucherDetailsButton } from "../../_shared/VoucherDetailsButton"
import CustomerInfo from "../../_shared/orders/CustomerInfo"
import { getBundleParams } from "../../../utils"
import ErpState from "../../_shared/orders/ErpState"

type Props = PropsWithChildren<{
    orders: OrderVoucherList[]
    shortCountryCode: string
    showCustomerColumn?: boolean
}>

export function WidgetOrderList({ orders, shortCountryCode, showCustomerColumn, ...rest }: Props) {
    const { formatDate } = useLocalization()
    const { erpOrderState } = getBundleParams()

    const showCustomers = useMemo(() => {
        return showCustomerColumn && orders?.some((order) => !!order.workTaskInfos?.some((wt) => wt.customerInfo))
    }, [orders, showCustomerColumn])

    function getColumns() {
        const columns: TableColumnData[] = [
            {}, // orderNumbers
            {}, // orderDate
        ]

        if (showCustomers) {
            columns.push({}) // CustomerInfo
        }

        columns.push(
            {
                alignContent: CellContentPosition.right,
            } // LicensePlate
        )

        columns.push(
            { alignContent: CellContentPosition.left } // VehicleInfo
        )
        if (erpOrderState) {
            columns.push({}) // ErpState
        }
        columns.push(
            { alignContent: CellContentPosition.right } // VoucherDetailsButton
        )

        return columns
    }

    function getCellData(order: OrderVoucherList) {
        const data: TableCellData[] = [
            { displayValue: order.orderNumbers?.find((orderNumber) => orderNumber.type === OrderIdSource.Wholesaler)?.number, id: "1" },
            { displayValue: formatDate(order.orderDate, "d"), id: "2" },
        ]

        const firstWorkTaskInfo = order.workTaskInfos?.first()

        if (showCustomers) {
            data.push({ displayValue: <CustomerInfo order={order} />, id: "3" })
        }

        if (firstWorkTaskInfo?.vehicleInfo?.plateId) {
            data.push({
                displayValue: <LicensePlate value={firstWorkTaskInfo.vehicleInfo.plateId} countryCode={shortCountryCode} width="100%" />,
                id: "4",
            })
        } else {
            data.push({
                displayValue: "",
                id: "4",
            })
        }

        data.push({
            displayValue: (
                <Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{firstWorkTaskInfo?.vehicleInfo?.displayName}</Box>
            ),
            id: "5",
            minWidth: 0,
        })
        if (erpOrderState) {
            data.push({ displayValue: <ErpState orderVouchers={orders} order={order} />, id: "6" })
        }

        data.push({
            displayValue: (
                <VoucherDetailsButton variant="small" voucherId={order.orderVoucherId} voucherType={VoucherType.Order} buttonVariant="text" />
            ),
            id: "7",
        })

        return data
    }

    const displayData = orders.map((order) => ({
        cells: getCellData(order),
        id: `${order.orderVoucherId}`,
        customRow: false,
        active: false,
    }))

    if (!displayData.length) {
        return <NoResultHint />
    }

    return <StyledTableWithoutPadding variant="small" columns={getColumns()} rows={displayData} rowCap={4} headerStyle="hidden" {...rest} />
}
